import React from "react";
import { ClipboardCheck, Truck, FileText, Printer } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const DELIVERY_MODES = [
  { value: "collection", label: "Collection" },
  { value: "courier", label: "Courier" },
  { value: "pte_deliver", label: "PTE to deliver" },
];
const OUTCOMES = [
  { value: "", label: "— No change —" },
  { value: "ready_for_delivery", label: "In Dispatch" },
  { value: "dispatched", label: "Ready for Collection" },
  { value: "quote_declined", label: "Quote Declined — Return to Customer" },
];
const PAYMENTS = [
  { value: "account", label: "Account" },
  { value: "cod", label: "COD" },
  { value: "received", label: "Received" },
  { value: "not_required", label: "Not required" },
];

// SPT-PTE-2026-0071 — lines with no quote yet form their own group.
const NO_QUOTE = "__no_quote__";

export async function openCustomerFile(jobId) {
  try {
    const res = await api.get(`/jobs/${jobId}/customer-file.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiErr(e)); }
}

export async function openReceivingPdf(jobId) {
  try {
    const res = await api.get(`/jobs/${jobId}/receiving-pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiErr(e)); }
}


function Check({ id, label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0" }}>
      <input type="checkbox" checked={!!checked} data-testid={`closeout-${id}`}
        onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function L({ children }) {
  return <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
    textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>{children}</label>;
}

export function DispatchCloseoutModal({ jobId, onClose, onDone, itemIds, itemsSummary }) {
  const [co, setCo] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [overwriteExisting, setOverwriteExisting] = React.useState(false);
  const propScoped = Array.isArray(itemIds) && itemIds.length > 0;
  // SPT-PTE-2026-0071 — opened without a line selection (e.g. from the
  // Dispatch list), offer "Which quote(s)?" so staff close out exactly one
  // quote's lines, or several quotes together (e.g. a customer who sends one
  // PO covering several quotes), instead of the whole DN.
  const [jobItems, setJobItems] = React.useState(null);
  const [quotePick, setQuotePick] = React.useState(() => new Set());
  React.useEffect(() => {
    if (propScoped) return;
    api.get(`/jobs/${jobId}`)
      .then((r) => setJobItems(r.data.items || []))
      .catch(() => setJobItems([]));
  }, [jobId, propScoped]);
  const quoteGroups = React.useMemo(() => {
    const its = jobItems || [];
    const qs = [...new Set(its.map((i) => i.quote_number).filter(Boolean))];
    const none = its.filter((i) => !i.quote_number).length;
    return [
      ...qs.map((q) => ({ key: q, label: `Q ${q}`, count: its.filter((i) => i.quote_number === q).length })),
      ...(none ? [{ key: NO_QUOTE, label: "Not yet quoted", count: none }] : []),
    ];
  }, [jobItems]);
  const showQuotePicker = !propScoped && quoteGroups.length > 1;
  const scopeIds = propScoped ? itemIds
    : (jobItems || []).filter((i) => quotePick.has(i.quote_number || NO_QUOTE)).map((i) => i.id);
  const scoped = scopeIds.length > 0;
  // Never let the DN header's quote silently pre-fill a multi-quote close-out
  // (saving without ticking a quote would copy it onto every unquoted line —
  // the exact SPT-PTE-2026-0071 problem). Lines picked in the Job view that
  // share ONE quote get that quote; several quotes -> left blank so each line
  // keeps its own; none quoted -> previous behaviour (header quote).
  const quotePrefillDone = React.useRef(false);
  React.useEffect(() => {
    if (!co || quotePrefillDone.current) return;
    if (propScoped && Array.isArray(itemsSummary)) {
      quotePrefillDone.current = true;
      const qs = [...new Set(itemsSummary.map((i) => i.quote_number).filter(Boolean))];
      if (qs.length === 1) setCo((c) => ({ ...c, quote_number: qs[0] }));
      else if (qs.length > 1) setCo((c) => ({ ...c, quote_number: "" }));
    } else if (showQuotePicker) {
      quotePrefillDone.current = true;
      setCo((c) => ({ ...c, quote_number: "" }));
    }
  }, [co, propScoped, itemsSummary, showQuotePicker]);

  React.useEffect(() => {
    api.get(`/jobs/${jobId}/closeout`)
      .then((r) => setCo(r.data))
      .catch((e) => { toast.error(apiErr(e)); onClose?.(); });
  }, [jobId, onClose]);

  const set = (k, v) => setCo((c) => ({ ...c, [k]: v }));

  const togglePick = (key) => {
    const next = new Set(quotePick);
    if (next.has(key)) next.delete(key); else next.add(key);
    setQuotePick(next);
    // One quote ticked -> prefill its number. Several -> leave it blank so
    // each line keeps its own quote number.
    const real = [...next].filter((k) => k !== NO_QUOTE);
    if (real.length === 1) set("quote_number", real[0]);
    else set("quote_number", "");
  };

  const isQuoteDeclined = co?.outcome === "quote_declined";

  const save = async () => {
    if (co.outcome === "dispatched" && !co.po_number) {
      toast.error("PO number is required before marking Ready for Collection.");
      return;
    }
    if (co.outcome === "quote_declined" && !(co.remarks || "").trim()) {
      toast.error("Please provide a reason in Remarks for the declined quote.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        check_delivery_note: co.check_delivery_note,
        print_stickers: co.print_stickers,
        sticker_check_affix: co.sticker_check_affix,
        outcome: co.outcome || null,
        delivery_mode: co.delivery_mode || "collection",
        delivery_date: co.delivery_date || null,
        delivered_collected_by: co.delivered_collected_by || null,
        courier: co.courier || null,
        payment_indicator: co.payment_indicator || null,
        quote_number: co.quote_number || null,
        po_number: co.po_number || null,
        invoice_number: co.invoice_number || null,
        admin_sign: co.admin_sign || null,
        admin_sign_date: co.admin_sign_date || null,
        doc_control_sign: co.doc_control_sign || null,
        doc_control_sign_date: co.doc_control_sign_date || null,
        remarks: co.remarks || null,
        overwrite_existing: overwriteExisting,
        item_ids: scoped ? scopeIds : undefined,
      };
      const r = await api.put(`/jobs/${jobId}/closeout`, body);
      const n = (r.data.closed_items || []).length;
      const pendingDispatch = (r.data.pending_dispatch_items || []).length;
      const scopeNote = scoped ? ` (${r.data.items_affected || scopeIds.length} selected line(s) only)` : "";
      if (pendingDispatch) {
        toast.warning(`Close-out saved${scopeNote} · ${pendingDispatch} item(s) marked paid but not yet dispatched — ` +
          `they'll stay "In Dispatch" until handed over, then close automatically`, { duration: 8000 });
      } else {
        toast.success(n ? `Close-out saved${scopeNote} · ${n} item(s) closed and off the active list`
                        : `Close-out saved${scopeNote}`);
      }
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!co) return <Modal open title="Loading…" onClose={onClose} />;

  return (
    <Modal open onClose={onClose} icon={ClipboardCheck} title="Dispatch close-out"
      subtitle={`DN ${co.delivery_note_number || ""} · ${co.customer_name || ""}${scoped ? ` · ${scopeIds.length} of ${co.total_items || "?"} line(s) selected` : ""}`} wide
      footer={<>
        <button className="btn ghost" onClick={() => openCustomerFile(jobId)} data-testid="closeout-print">
          <Printer size={14} /> Customer File (PDF)
        </button>
        <span style={{ flex: 1 }} />
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="closeout-cancel">Cancel</button>
        <button className="btn gold" onClick={save} disabled={busy} data-testid="closeout-save">
          <Truck size={14} /> {busy ? "Saving…" : scoped ? `Save close-out · ${scopeIds.length} line(s)` : "Save close-out"}
        </button>
      </>}>
      {showQuotePicker && (
        <div className="card pad" style={{ marginBottom: 12, fontSize: 12.5 }} data-testid="closeout-quote-picker">
          <div className="t-strong" style={{ marginBottom: 6 }}>Which quote(s) are you closing out?</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {quoteGroups.map((g) => (
              <label key={g.key} className={quotePick.has(g.key) ? "badge b-gold" : "badge"}
                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                  border: "1px solid var(--line)", padding: "4px 10px", fontSize: 12 }}
                data-testid={`closeout-quote-pick-${g.key === NO_QUOTE ? "none" : g.key}`}>
                <input type="checkbox" checked={quotePick.has(g.key)} onChange={() => togglePick(g.key)} />
                {g.label} · {g.count} line{g.count !== 1 ? "s" : ""}
              </label>
            ))}
          </div>
          <div className="t-mut" style={{ fontSize: 11.5, marginTop: 6 }}>
            Tick one or more. If a customer sends one PO for several quotes, tick all of those quotes together.
          </div>
        </div>
      )}
      {scoped ? (
        <div className="card pad" style={{ background: "#EAF3FF", marginBottom: 12, fontSize: 12.5 }}
          data-testid="closeout-scope-banner">
          <b>Only the {scopeIds.length} selected line(s) will be closed out</b> — every other line on
          this job is left untouched.
          {itemsSummary && itemsSummary.length > 0 && (
            <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
              {itemsSummary.map((it) => (
                <li key={it.id} data-testid={`closeout-scope-item-${it.id}`}>
                  {it.description || "—"}{it.serial_number ? ` · SN ${it.serial_number}` : it.issue_number ? ` · IN ${it.issue_number}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="card pad" style={{ background: "#F4F4F4", marginBottom: 12, fontSize: 12.5 }}
          data-testid="closeout-scope-banner-all">
          {showQuotePicker ? "No quote is ticked above" : "No lines are selected in the table below"}, so this close-out will apply to <b>every line on this job</b>.
          {showQuotePicker ? " Tick a quote above to close out only its lines." : " Close this modal and tick specific lines first if you only want to close out a subset."}
        </div>
      )}
      <div className="t-strong" style={{ fontSize: 12.5, marginBottom: 6 }}>Dispatch checklist</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 12 }}>
        <Check id="check-dn" label="Check delivery note" checked={co.check_delivery_note} onChange={(v) => set("check_delivery_note", v)} />
        <Check id="print-stickers" label="Print stickers" checked={co.print_stickers} onChange={(v) => set("print_stickers", v)} />
        <Check id="sticker-affix" label="Sticker check & affix" checked={co.sticker_check_affix} onChange={(v) => set("sticker_check_affix", v)} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div><L>Outcome</L>
          <select className="input" value={co.outcome || ""} data-testid="closeout-outcome"
            onChange={(e) => set("outcome", e.target.value)}>
            {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div><L>Delivery method</L>
          <select className="input" value={co.delivery_mode || "collection"} data-testid="closeout-mode"
            onChange={(e) => set("delivery_mode", e.target.value)}>
            {DELIVERY_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div><L>Delivery / collection date</L>
          <input type="date" className="input" value={co.delivery_date || ""} data-testid="closeout-date"
            onChange={(e) => set("delivery_date", e.target.value)} />
        </div>
        <div><L>Delivered / collected by</L>
          <input className="input" value={co.delivered_collected_by || ""} data-testid="closeout-by"
            onChange={(e) => set("delivered_collected_by", e.target.value)} placeholder="Name" />
        </div>
      </div>
      <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 12 }}>
        Choosing an outcome sets the item's status directly — "In Dispatch" keeps it in
        PTE's control; "Ready for Collection" marks it as handed over (requires a PO number below) and is
        now the terminal step before Close-out — no more separate Collected/Delivered stops.
        "Quote Declined" is for a customer who declined the quote and gets the item back as-is — no
        invoice, no payment, and the equipment's calibration due date is cleared so it stops generating
        recall reminders. Leave "— No change —" to update quote/PO/invoice/payment only.
      </div>

      {isQuoteDeclined ? (
        <div className="card pad" style={{ background: "#FDECEC", marginBottom: 12, fontSize: 12.5 }}
          data-testid="closeout-quote-declined-note">
          No invoice, PO or payment will be captured — those fields are auto-set to "N/A". On save this
          item closes and returns to the Equipment Register with a <b>Quote Declined</b> status, and its
          calibration due date is cleared entirely (no further recall). Please state the reason below.
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div><L>Payment</L>
              <select className="input" value={co.payment_indicator || ""} data-testid="closeout-payment"
                onChange={(e) => set("payment_indicator", e.target.value)}>
                <option value="">—</option>
                {PAYMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div><L>Quote no.</L>
              <input className="input mono" value={co.quote_number || ""} data-testid="closeout-quote"
                onChange={(e) => set("quote_number", e.target.value)} /></div>
            <div><L>PO no.</L>
              <input className="input mono" value={co.po_number || ""} data-testid="closeout-po"
                onChange={(e) => set("po_number", e.target.value)} /></div>
            <div><L>Invoice no.</L>
              <input className="input mono" value={co.invoice_number || ""} data-testid="closeout-invoice"
                onChange={(e) => set("invoice_number", e.target.value)} /></div>
          </div>
          <Check id="overwrite-existing" label={scoped
            ? `Apply Quote/PO/Invoice above to ALL ${scopeIds.length} selected line(s) (overwrites any of them that already has its own number)`
            : "Apply Quote/PO/Invoice above to ALL equipment lines on this job (overwrites any line that already has its own number)"}
            checked={overwriteExisting} onChange={setOverwriteExisting} />
          <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 12 }}>
            Off (default): only lines with a blank Quote/PO/Invoice pick up the value above — protects DNs
            where different lines are legitimately invoiced under different references. On: every {scoped ? "selected" : ""} line{scoped ? "" : " on this job"} is
            force-set to the values above, no exceptions.
          </div>
          <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 12 }}>
            Account → awaiting payment · COD / Received → paid · Not required → no payment owed.
            Sage stays the accounting system. When the outcome is Ready for Collection
            <strong> and</strong> payment is Received / COD / Not required, the item closes and leaves
            the active workflow. Unpaid Account items stay open under "Awaiting Payment" until paid.
          </div>
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div><L>Admin sign</L>
          <input className="input" value={co.admin_sign || ""} data-testid="closeout-admin-sign"
            onChange={(e) => set("admin_sign", e.target.value)} placeholder="Name" /></div>
        <div><L>Admin sign date</L>
          <input type="date" className="input" value={co.admin_sign_date || ""} data-testid="closeout-admin-date"
            onChange={(e) => set("admin_sign_date", e.target.value)} /></div>
        <div><L>Document control sign</L>
          <input className="input" value={co.doc_control_sign || ""} data-testid="closeout-doc-sign"
            onChange={(e) => set("doc_control_sign", e.target.value)} placeholder="Name" /></div>
        <div><L>Document control date</L>
          <input type="date" className="input" value={co.doc_control_sign_date || ""} data-testid="closeout-doc-date"
            onChange={(e) => set("doc_control_sign_date", e.target.value)} /></div>
      </div>

      <div><L>Remarks{isQuoteDeclined ? " (reason for decline — required)" : ""}</L>
        <textarea className="input" rows={2} value={co.remarks || ""} data-testid="closeout-remarks"
          onChange={(e) => set("remarks", e.target.value)} placeholder="Additional customer info / remarks" /></div>
    </Modal>
  );
}
